<script>
  import { createEventDispatcher } from "svelte";

  export let users = [];
  export let placeholder = "Search by Name, Mobile, PAN, or UCC Tag...";
  export let value = "";
  export let filterUntaggedOnly = false;
  export let disabled = false;
  export let id = "userSearchInput";

  const dispatch = createEventDispatcher();
  let isDropdownOpen = false;

  $: filteredUsers = users
    .filter((u) => {
      if (filterUntaggedOnly && (u.client_tag || u.ucc)) {
        return false;
      }
      if (!value.trim()) return true;
      const q = value.toLowerCase();
      return (
        (u.fullName && u.fullName.toLowerCase().includes(q)) ||
        (u.mobile && u.mobile.includes(q)) ||
        (u.pan && u.pan.toLowerCase().includes(q)) ||
        (u.ucc && u.ucc.toLowerCase().includes(q)) ||
        (u.client_tag && u.client_tag.toLowerCase().includes(q))
      );
    })
    .slice(0, 15);

  function handleSelect(user) {
    value = `${user.fullName} (${user.mobile})`;
    isDropdownOpen = false;
    dispatch("select", user);
  }

  function handleInput() {
    isDropdownOpen = true;
    dispatch("input", value);
  }

  function handleFocus() {
    isDropdownOpen = true;
  }

  function handleBlur() {
    // Delay closing dropdown so click events on items register
    setTimeout(() => {
      isDropdownOpen = false;
    }, 200);
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") {
      isDropdownOpen = false;
      dispatch("enter", value);
    }
  }
</script>

<div class="typeahead-container">
  <input
    {id}
    type="text"
    class="typeahead-input"
    {placeholder}
    {disabled}
    bind:value
    autocomplete="off"
    autocorrect="off"
    autocapitalize="off"
    spellcheck="false"
    on:focus={handleFocus}
    on:blur={handleBlur}
    on:input={handleInput}
    on:keydown={handleKeyDown}
  />

  {#if isDropdownOpen && !disabled && filteredUsers.length > 0}
    <div class="typeahead-dropdown">
      {#each filteredUsers as u}
        <button
          type="button"
          class="typeahead-row"
          on:mousedown|preventDefault={() => handleSelect(u)}
        >
          <div class="avatar-circle">
            {(u.fullName || "U").charAt(0).toUpperCase()}
          </div>
          <div class="user-meta">
            <div class="user-name">{u.fullName}</div>
            <div class="user-sub">
              📱 {u.mobile} | PAN: {u.pan || "N/A"}
            </div>
          </div>
          <div>
            {#if u.client_tag || u.ucc}
              <span class="tag-badge active-tag">
                {u.client_tag || u.ucc}
              </span>
            {:else}
              <span class="tag-badge no-tag">
                No Tag
              </span>
            {/if}
          </div>
        </button>
      {/each}
    </div>
  {/if}
</div>

<style>
  .typeahead-container {
    position: relative;
    width: 100%;
  }

  .typeahead-input {
    width: 100%;
    box-sizing: border-box;
    padding: 10px 14px;
    font-size: 14px;
    border: 1.5px solid #cbd5e1;
    border-radius: 8px;
    outline: none;
    background: #ffffff;
    color: #0f172a;
    transition: all 0.2s ease;
  }

  .typeahead-input:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  }

  .typeahead-input:disabled {
    background: #f1f5f9;
    color: #64748b;
    cursor: not-allowed;
  }

  .typeahead-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
    max-height: 260px;
    overflow-y: auto;
    z-index: 1000;
  }

  .typeahead-row {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border: none;
    background: transparent;
    text-align: left;
    cursor: pointer;
    border-bottom: 1px solid #f1f5f9;
    transition: background 0.15s;
  }

  .typeahead-row:last-child {
    border-bottom: none;
  }

  .typeahead-row:hover {
    background: #f8fafc;
  }

  .avatar-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #e2e8f0;
    color: #334155;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 13px;
    flex-shrink: 0;
  }

  .user-meta {
    flex: 1;
    min-width: 0;
  }

  .user-name {
    font-weight: 600;
    font-size: 13px;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .user-sub {
    font-size: 11px;
    color: #64748b;
    margin-top: 2px;
  }

  .tag-badge {
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    font-family: monospace;
    white-space: nowrap;
  }

  .active-tag {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .no-tag {
    background: #f1f5f9;
    color: #94a3b8;
  }
</style>
